import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Filter, Plus, Search } from "lucide-react";
import { toast } from "sonner";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TablePagination } from "~/components/ui/table-pagination";
import { useDeletePurchaseOrder, usePurchaseOrders } from "~/hooks/purchasing";
import { useActiveSuppliers } from "~/hooks/purchasing";
import { usePagination } from "~/hooks/usePagination";
import type { PurchaseOrder, PurchaseOrderFilters } from "~/types/purchasing";
import {
  ORDER_TYPE_LABELS,
  PURCHASE_ORDER_STATUS_LABELS,
} from "~/types/purchasing";
import { generatePurchaseOrderPdf } from "~/utils/purchaseOrderPdf";

import { PurchaseOrdersTable } from "./components/PurchaseOrdersTable";

export function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<PurchaseOrderFilters>({});
  const [poNumberSearch, setPoNumberSearch] = useState("");
  const [orderToDelete, setOrderToDelete] = useState<PurchaseOrder | null>(
    null
  );

  const { data: orders = [], isLoading } = usePurchaseOrders(filters);
  const { data: suppliers = [] } = useActiveSuppliers();
  const deleteOrder = useDeletePurchaseOrder();

  // OC number is matched client-side against the padded display value (e.g.
  // "00123"), so typing "123", "0123" or "00123" all find the same order.
  const filteredOrders = useMemo(() => {
    const digits = poNumberSearch.replace(/\D/g, "");
    if (!digits) return orders;
    return orders.filter((order) =>
      String(order.po_number).padStart(5, "0").includes(digits)
    );
  }, [orders, poNumberSearch]);

  const PAGE_SIZE = 10;
  const { page, setPage, pageCount, pageItems, total } = usePagination(
    filteredOrders,
    PAGE_SIZE
  );

  const handleRowClick = (order: PurchaseOrder) => {
    navigate(`/purchasing/orders/${order.id}`);
  };

  const handleViewPdf = async (order: PurchaseOrder) => {
    await generatePurchaseOrderPdf(order);
  };

  const handleEdit = (order: PurchaseOrder) => {
    navigate(`/purchasing/orders/${order.id}?edit=true`);
  };

  const handleDelete = (order: PurchaseOrder) => {
    setOrderToDelete(order);
  };

  const confirmDelete = async () => {
    if (!orderToDelete) return;

    try {
      await deleteOrder.mutateAsync(orderToDelete.id);
      toast.success("Orden de compra eliminada");
      setOrderToDelete(null);
    } catch (error) {
      toast.error("Error al eliminar la orden de compra");
    }
  };

  const handleFilterChange = (
    key: keyof PurchaseOrderFilters,
    value: string
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === "all" ? undefined : value,
    }));
  };

  return (
    <PageContainer>
      <PageHeader
        title="Órdenes de Compra"
        description="Gestiona las órdenes de compra de ingredientes y materiales"
        actions={
          <Button onClick={() => navigate("/purchasing/orders/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Nueva Orden
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                inputMode="numeric"
                placeholder="Buscar N° OC..."
                className="h-9 pl-8"
                value={poNumberSearch}
                onChange={(e) => setPoNumberSearch(e.target.value)}
              />
            </div>

            <Select
              value={filters.status || "all"}
              onValueChange={(value) => handleFilterChange("status", value)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(PURCHASE_ORDER_STATUS_LABELS).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>

            <Select
              value={filters.order_type || "all"}
              onValueChange={(value) => handleFilterChange("order_type", value)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos los tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {Object.entries(ORDER_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <SearchableSelect
              value={filters.supplier_id || "all"}
              onValueChange={(value) =>
                handleFilterChange("supplier_id", value)
              }
              options={[
                { value: "all", label: "Todos los proveedores" },
                ...suppliers.map((supplier) => ({
                  value: supplier.id,
                  label: supplier.name,
                  searchTerms: `${supplier.name} ${supplier.rut || ""}`,
                })),
              ]}
              placeholder="Todos los proveedores"
              searchPlaceholder="Buscar proveedor..."
              emptyMessage="No se encontraron proveedores"
              className="h-9"
            />

            <Input
              type="date"
              className="h-9"
              value={filters.dateFrom || ""}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
            />

            <Input
              type="date"
              className="h-9"
              value={filters.dateTo || ""}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <PurchaseOrdersTable
            orders={pageItems}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            onViewPdf={handleViewPdf}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          <TablePagination
            page={page}
            pageCount={pageCount}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            itemLabel="órdenes"
            className="border-t px-4 py-3"
          />
        </CardContent>
      </Card>

      <AlertDialog
        open={!!orderToDelete}
        onOpenChange={() => setOrderToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Orden de Compra</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar la orden de compra #
              {orderToDelete?.po_number
                ? String(orderToDelete.po_number).padStart(5, "0")
                : ""}
              ? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
